import { Box, Button, Divider, Flex, Menu, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useFunds } from '../../hooks/useFunds';
import classes from 'src/UI/page-nav/UnderlineTabs.module.css';

type FundSearchProps = {
  width?: number;
  prefix?: string;
};

type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
};

export function FundSearch({ width, prefix }: FundSearchProps) {
  return prefix ? (
    <Flex gap="xxs" align="center">
      <Text size={'xxl'} fw={600}>
        {prefix}
      </Text>
      <Box pt="xxs">
        <FundSearchMenu width={width} />
      </Box>
    </Flex>
  ) : (
    <FundSearchMenu />
  );
}

function FundSearchMenu({ width }: FundSearchProps) {
  const { funds } = useFunds();
  const [search, setSearch] = useState('');
  const [menuOpened, setMenuOpened] = useState(false);
  const theme = useMantineTheme();
  const { pathname } = useLocation();

  const currentFundId = pathname.split('/').pop();
  const currentFund = funds.find(f => f.id === currentFundId);
  const fundName = currentFund?.name || 'Select Fund';

  const results =
    search.length > 0
      ? funds
          .filter(
            f =>
              f.name.toLowerCase().includes(search.toLowerCase()) ||
              f.code.toLowerCase().includes(search.toLowerCase()),
          )
          .slice(0, 3)
      : [];

  return (
    <Menu
      position="bottom-start"
      shadow="md"
      opened={menuOpened}
      onClose={() => setMenuOpened(false)}
      classNames={{
        dropdown: classes.clientSearchDropdown,
      }}
    >
      <Menu.Target>
        <Button
          variant="subtle"
          style={{
            fontWeight: 600,
            color: theme.colors.brand[6],
            fontSize: theme.fontSizes.xxl,
          }}
          px={0}
          styles={{
            root: {
              width,
            },
            label: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
          }}
          rightSection={
            menuOpened ? (
              <IconChevronUp size={18} color={theme.colors.brand[7]} />
            ) : (
              <IconChevronDown size={18} color={theme.colors.brand[7]} />
            )
          }
          onClick={() => setMenuOpened(o => !o)}
        >
          {fundName}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Flex direction="column" gap="xxs" w={220}>
          <SearchInput value={search} onChange={setSearch} />
          {results.length > 0 && <Divider color={'gray.0'} />}
          {results.map(fundResult => {
            const href = `/company/funds/${fundResult.id}`;
            return (
              <Menu.Item
                component={Link}
                key={fundResult.id}
                to={href}
                onClick={() => {
                  setSearch('');
                  setMenuOpened(false);
                }}
              >
                {fundResult.name}
              </Menu.Item>
            );
          })}
        </Flex>
      </Menu.Dropdown>
    </Menu>
  );
}

function SearchInput({ value, onChange }: SearchInputProps) {
  function focusInput(el: HTMLInputElement | null) {
    if (el) {
      requestAnimationFrame(() => {
        el.focus();
      });
    }
  }

  return (
    <TextInput
      ref={focusInput}
      placeholder="Search funds"
      value={value}
      onChange={e => onChange(e.target.value)}
      classNames={{
        input: classes.clientSearchInput,
      }}
      rightSection={<IconSearch color={'#E5E5E5'} />}
      autoFocus
    />
  );
}
